import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowUpRight, BookOpen, FileText, CalendarClock, PlayCircle } from 'lucide-react';
import { api } from '../lib/api';
import SEOHead from '../components/common/SEOHead';

/**
 * Seller Centre — trust-first education hub.
 * Twelve editorial pillars + three companion tools (home value report,
 * listing consultation, video library). Trust before appointment.
 */
export default function SellerCentre() {
    const { data } = useQuery({
        queryKey: ['seller-centre'],
        queryFn: async () => (await api.get('/seller-centre')).data,
    });
    const items = data?.items || [];

    return (
        <div className="bg-white">
            <SEOHead
                title="Seller Centre — Southern Ontario"
                description="Southern Ontario's most valuable seller education hub. Pricing strategy, renovation ROI, negotiation, closing, and the home value report."
                path="/seller-centre"
            />

            {/* ==================== COVER ==================== */}
            <section className="border-b border-neutral-200 bg-[#FAFAF8]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-20 lg:pb-28">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                        <span className="inline-block w-6 h-px bg-neutral-400" />
                        The Seller Centre · Southern Ontario
                    </div>
                    <h1
                        className="font-editorial mt-8 leading-[1.02] text-black max-w-4xl"
                        style={{ fontSize: 'clamp(52px, 8vw, 108px)', letterSpacing: '-0.03em' }}
                        data-testid="seller-centre-title"
                    >
                        Trust first.
                        <br />
                        <span className="italic text-neutral-500">Listing appointment second.</span>
                    </h1>
                    <p className="mt-8 font-editorial text-neutral-700 max-w-2xl" style={{ fontSize: 'clamp(17px, 1.3vw, 21px)', lineHeight: 1.55 }}>
                        Twelve pillars — the complete playbook for selling a home in Southern Ontario.
                        Pricing, prep, marketing, negotiation, closing. Independent research, written
                        the way we&apos;d brief a family member.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-3">
                        <Link
                            to="/seller-centre/pricing-strategy"
                            data-testid="seller-centre-start"
                            className="inline-flex items-center gap-2 bg-brand-navy text-white px-7 py-4 text-[14px] font-medium hover:bg-neutral-800"
                            style={{ letterSpacing: '0.02em' }}
                        >
                            <BookOpen className="w-4 h-4" /> Start with pricing
                        </Link>
                        <Link
                            to="/sellers/home-value-report"
                            data-testid="seller-centre-value-cta"
                            className="inline-flex items-center gap-2 border border-neutral-400 text-black px-7 py-4 text-[14px] font-medium hover:bg-brand-navy hover:text-white transition-colors"
                           
                        >
                            <FileText className="w-4 h-4" /> Request the home value report
                        </Link>
                    </div>
                </div>
            </section>

            {/* ==================== COMPANION TOOLS ==================== */}
            <section className="border-b border-neutral-200 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-neutral-200">
                    <CompanionTile
                        to="/sellers/home-value-report"
                        overline="Tool · Gated"
                        title="Home Value Report"
                        blurb="A defensible pricing document, prepared within 24 hours from comparable sales and current active listings on your block."
                        icon={<FileText className="w-4 h-4" />}
                        testid="seller-tool-home-value"
                    />
                    <CompanionTile
                        to="/sellers/consultation"
                        overline="Appointment"
                        title="Book a Listing Consultation"
                        blurb="Thirty minutes, on your kitchen table or on video. We&apos;ll walk through comparable sales, your objective, and the honest go-to-market plan."
                        icon={<CalendarClock className="w-4 h-4" />}
                        testid="seller-tool-consultation"
                    />
                    <CompanionTile
                        to="/videos"
                        overline="Watch"
                        title="Seller Video Library"
                        blurb="Neighbourhood tours, market updates, and case studies. Curated to build context before you list."
                        icon={<PlayCircle className="w-4 h-4" />}
                        testid="seller-tool-videos"
                    />
                </div>
            </section>

            {/* ==================== PILLAR GRID ==================== */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
                <div className="flex items-baseline justify-between mb-10">
                    <h2 className="font-editorial leading-tight" style={{ fontSize: 'clamp(28px, 3vw, 44px)', letterSpacing: '-0.02em' }}>
                        The twelve pillars.
                    </h2>
                    <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data hidden md:block">
                        {items.length} deep reads
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0 border-l border-t border-neutral-200">
                    {items.map((c, i) => (
                        <motion.div
                            key={c.slug}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-40px' }}
                            transition={{ duration: 0.5, delay: (i % 4) * 0.05, ease: [0.22, 0.61, 0.36, 1] }}
                            className="border-r border-b border-neutral-200"
                        >
                            <Link
                                to={`/seller-centre/${c.slug}`}
                                data-testid={`seller-pillar-${c.slug}`}
                                className="group block relative bg-white hover:bg-brand-navy hover:text-white transition-colors duration-500 h-full"
                            >
                                <div className="relative aspect-[4/3] overflow-hidden bg-neutral-200">
                                    <img src={c.hero_image} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                                    <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0) 60%)' }} />
                                    <div className="absolute top-3 left-3 font-mono-data text-[10px] tracking-[0.24em] uppercase text-white/85 tabular">
                                        {String(i + 1).padStart(2, '0')}
                                    </div>
                                </div>
                                <div className="p-5 lg:p-6 min-h-[180px] flex flex-col">
                                    <div className="font-editorial text-[22px] tracking-editorial leading-tight group-hover:italic">
                                        {c.name}
                                    </div>
                                    <p className="mt-3 text-[13px] leading-relaxed text-neutral-600 group-hover:text-white/80 flex-1">
                                        {c.kicker}
                                    </p>
                                    <div className="mt-4 flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase text-neutral-500 group-hover:text-white/70 font-mono-data">
                                        Read <ArrowUpRight className="w-3 h-3" />
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ==================== BOTTOM CTA ==================== */}
            <section className="bg-brand-navy text-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-white/60 font-mono-data flex items-center gap-3">
                            <span className="inline-block w-6 h-px bg-white/60" />
                            When you&apos;re ready
                        </div>
                        <h2 className="font-editorial mt-6 leading-[1.03]" style={{ fontSize: 'clamp(36px, 4.8vw, 68px)', letterSpacing: '-0.02em' }}>
                            The reading gets you halfway. The conversation gets you home.
                        </h2>
                        <p className="mt-6 font-editorial text-white/80 max-w-xl" style={{ fontSize: '18px', lineHeight: 1.55 }}>
                            Book a listing consultation. We&apos;ll walk through the last twelve sales
                            on your block, the honest ceiling, and the go-to-market plan that hits
                            it. No listing agreement discussed on the first visit.
                        </p>
                    </div>
                    <div className="lg:col-span-4">
                        <Link
                            to="/sellers/consultation"
                            data-testid="seller-centre-consultation-cta"
                            className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 text-[14px] font-medium hover:bg-neutral-200"
                            style={{ letterSpacing: '0.02em' }}
                        >
                            Book a listing consultation <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}


function CompanionTile({ to, overline, title, blurb, icon, testid }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className="group block border-r border-b border-neutral-200 p-6 lg:p-8 bg-white hover:bg-brand-navy hover:text-white transition-colors duration-400"
        >
            <div className="flex items-center gap-2 text-[10.5px] tracking-[0.24em] uppercase text-neutral-500 group-hover:text-white/70 font-mono-data">
                {icon}
                {overline}
            </div>
            <div className="font-editorial mt-4 leading-tight group-hover:italic" style={{ fontSize: '26px', letterSpacing: '-0.01em' }}>
                {title}
            </div>
            <p className="mt-3 text-[13.5px] leading-relaxed text-neutral-600 group-hover:text-white/80">
                {blurb}
            </p>
            <div className="mt-5 flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase font-mono-data text-neutral-500 group-hover:text-white/80">
                Open <ArrowUpRight className="w-3 h-3" />
            </div>
        </Link>
    );
}
